import { comparer } from 'mobx';

import { codeSplit, Controller, FetchOptions, FetchPageOptions, merge, PageStore } from 'bernie-core';
import { Localization } from 'bernie-l10n';
import { PageData } from 'bernie-http';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchCriteriaModel, SearchCriteriaUtils, singleUseQueryParams, untrackedQueryParams } from 'stores/models';
import { MultiItemStore } from 'stores/multi-item-store';

import { PageName } from 'constants/pages';

import { meta } from 'utils/meta';

import { buildFlexPageData } from 'utils/flex-seo-utils';
import { CollectionBusinessModel } from 'stores/multi-item-store-util';
import { ExtendedContextStore } from 'types/flex-definitions';

const { withoutKeys, withKeys } = merge;

export class InfositeController implements Controller {
  public bundles = ['infosite'];
  public component = codeSplit(() => import(/* webpackChunkName: "infosite" */ 'views/hotel/infosite.jsx'));
  public pageId = `${PageName.INFOSITE},H,30`;
  public path = '*/:lang?/:prefix?*h:hotelId.*';
  public exact = false;
  public routeName = 'infosite';
  public routeData = {
    rfrrNamespace: 'HOT.HIS',
    pageName: PageName.INFOSITE
  };
  public flexViewModelResponse;

  public fetchPageData(options: FetchPageOptions): Promise<PageData> {
    const context = options.context as ExtendedContextStore;
    const seoData = context && context.seoData;
    this.flexViewModelResponse = context && context.viewModelResponse;

    // Either build SEO data from Flex (his-flex-web)
    if (this.flexViewModelResponse) {
      return Promise.resolve(buildFlexPageData(this.flexViewModelResponse, this.pageId)) as Promise<PageData>;
    }

    // Or try to get data from Blacksmith from BFF
    if (seoData) {
      const seo = {
        seo: {
          canonical: seoData.url
        }
      };

      return Promise.resolve(seo as PageData);
    }

    return Promise.resolve(null as PageData);
  }

  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('InfositeController.fetch received invalid FetchOptions');
    }

    const { isServer, stores, query, params } = options;

    const context: ExtendedContextStore = stores.get<ExtendedContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const currentHotel: CurrentHotelStore = stores.get<CurrentHotelStore>('currentHotel');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');
    const multiItemBusinessModel: CollectionBusinessModel = multiItem && multiItem.cartInfo && multiItem.cartInfo.type;

    // infosite cares about hotelId URL param
    const { prevParams, ...nextParams } = params || { prevParams: undefined };

    // query params cleanup
    const singleUseCriteria: any = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...singleUseCriteria, 'prevQuery'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery = withoutKeys(untrackedKeys).merge({}, query);

    // 29690_LS_PWA_PLAYBACK_AREA
    if (multiItemBusinessModel && nextQuery) {
      nextQuery['businessModelFilter'] = multiItemBusinessModel;
    }

    const forceFetch = currentHotel.shouldFetch || currentHotel.shouldFetchOffers;

    if (forceFetch || !comparer.structural(nextParams, prevParams) || !comparer.structural(prevQuery, nextQuery)) {
      // TEMP FIX: check removal after 2018-05-01 with @dboerner
      this.removeRegionIdForSEMUrls(nextQuery);
      // =====================================================

      const parsedInfositeValues = SearchCriteriaUtils.parseInfositeValues(
        { ...nextQuery },
        new Localization(context.locale),
        context.localDateFormat
      );

      const incomingSearchCriteria = new SearchCriteriaModel(parsedInfositeValues);

      const criteriaChanged = this.haveCriteriaChanged(currentHotel.searchCriteria, incomingSearchCriteria);

      if (forceFetch || criteriaChanged || this.hasHotelChanged(prevParams, nextParams)) {
        currentHotel.setOfferData(withKeys(['startDate', 'endDate', 'adults', 'children']).merge({}, parsedInfositeValues));

        currentHotel.setHotelId(nextParams.hotelId);
        currentHotel.updateSearchCriteria(incomingSearchCriteria);
        if (!incomingSearchCriteria.isDateless) {
          currentHotel.clearDatelessContexts(context);
        }
        currentHotel.clearPoiData();

        const currentHotelFetch = currentHotel.fetch(context, singleUseCriteria);
        const flexViewModelResponse = this.flexViewModelResponse;

        if (
          flexViewModelResponse &&
          flexViewModelResponse.composition &&
          flexViewModelResponse.composition.page &&
          flexViewModelResponse.composition.page.regionList.length >= 1 &&
          flexViewModelResponse.composition.page.regionList[0].name === 'rooms' &&
          flexViewModelResponse.composition.page.regionList[0].modules.some(module => module.name === 'breadcrumbs')
        ) {
          currentHotel.setBreadcrumbsModel(flexViewModelResponse.composition.page.regionList);
        }

        //Only fetch reviews, when it hasn't been fetched yet
        const reviewsFetch = !currentHotel.hasPreFetchedReviews && currentHotel.fetchReviews(context, nextParams.hotelId, true);

        await currentHotelFetch;

        if (!page.seo) {
          const pageFetch = page.fetch(meta['hotel-infosite'], context.site, context.locale);
          await pageFetch;
        }

        if (reviewsFetch) {
          await reviewsFetch;
        }
      }

      if (!isServer) {
        currentHotel.fetchSimilarHotels(context);
      }
    }
  }

  public async willDeactivate(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('InfositeController.willDeactivate receieved an invalid StoreContainer');
    }

    const currentHotel: CurrentHotelStore = options.stores.get<CurrentHotelStore>('currentHotel');
    const context: ExtendedContextStore = options.stores.get<ExtendedContextStore>('context');

    if (currentHotel.isFailure || !currentHotel.offers || currentHotel.offers.error || !currentHotel.hotel || currentHotel.hotel.error) {
      currentHotel.shouldFetch = true;
    }

    currentHotel.clearRoomsFromOffers();
    currentHotel.shouldFetchOffers = true;
    currentHotel.loadingOffers = true;
    delete context.footer;
  }

  private removeRegionIdForSEMUrls(query): any {
    // TODO: SEM regionId fix (do not remove until SEM URLs have been fixed)
    if (query && /google/gi.test(query.semcid)) {
      delete query.regionId;
    }
  }

  private hasHotelChanged(prevParams, nextParams): boolean {
    if (prevParams && nextParams && prevParams.hotelId && nextParams.hotelId) {
      return prevParams.hotelId !== nextParams.hotelId;
    }

    return false;
  }

  private haveCriteriaChanged(previousModel: SearchCriteriaModel, newModel: SearchCriteriaModel): boolean {
    if (Boolean(newModel) && !Boolean(previousModel)) {
      return true;
    }

    return !comparer.structural(previousModel.toJSON(), newModel.toJSON());
  }
}
